import { BusinessDate } from '../date/business-date';
import { IsoWeek } from '../date/iso-week';
import type { DailyInspection, InspectionStatus } from '../planning/daily-inspection';
import type { WeeklyAllocation } from '../planning/weekly-allocation';
import type { ExecutionResourceId } from '../shared/ids';

/**
 * Agregação de horas para os indicadores (RN-043, RN-124).
 *
 * Tudo aqui é **função pura sobre coleções já carregadas**: nenhuma consulta, nenhum
 * repositório. É o que torna a virada do mês e a virada do ano testáveis sem banco, e é
 * também o que impede o painel de ter uma segunda conta de horas — disponível, planejado
 * e saldo saem exatamente do mesmo lugar que o planejamento semanal usa (`CapacityCalculator`).
 *
 * A unidade é sempre **quarto de hora inteiro** (ADR-106). Somar 45h em incrementos de 15
 * minutos fecha em 45h exatas, e nenhum indicador exibe dízima onde deveria haver hora
 * cheia.
 *
 * ## O que é "planejado" e o que é "realizado"
 *
 * A conferência não copia a alocação: ela **altera** a alocação e congela o total do dia
 * em `DailyInspection.plannedQuarterHours`. Daí a assimetria que este módulo respeita:
 *
 * - no nível de **recurso e dia**, o planejado é o congelado da conferência quando ela
 *   existe — é o único lugar onde o plano de antes do ajuste sobreviveu;
 * - o realizado só existe quando o dia foi conferido. Dia pendente não vale zero: vale
 *   **desconhecido**, e tratá-lo como zero faria toda semana em curso parecer parada.
 */

/** Um par recurso × dia — a menor célula de todo indicador de horas. */
export interface ResourceDayHours {
  readonly resourceId: ExecutionResourceId;
  readonly date: string;
  /** Ausente = recurso sem limite declarado (RN-047). Nunca zero por omissão. */
  readonly availableQuarterHours?: number;
  readonly plannedQuarterHours: number;
  /** Ausente enquanto o dia não é conferido — desconhecido, não zero. */
  readonly executedQuarterHours?: number;
  readonly status: InspectionStatus | 'NOT_INSPECTED';
}

export interface BuildResourceDaysInput {
  readonly days: readonly BusinessDate[];
  readonly resourceIds: readonly ExecutionResourceId[];
  readonly allocations: readonly WeeklyAllocation[];
  readonly inspections: readonly DailyInspection[];
  /** Capacidade do recurso no dia — vem do índice de capacidade, resolvido uma vez só. */
  readonly availableOn: (resourceId: ExecutionResourceId, date: BusinessDate) => number | undefined;
}

function dayKey(resourceId: string, date: string): string {
  return `${resourceId}|${date}`;
}

/**
 * Monta a malha recurso × dia do período.
 *
 * Só entram as células com **alguma coisa**: capacidade positiva, planejamento ou
 * conferência. Domingo de quem não trabalha domingo não vira uma linha de zeros que
 * dilui todo percentual do painel.
 */
export function buildResourceDays(input: BuildResourceDaysInput): ResourceDayHours[] {
  const plannedByCell = new Map<string, number>();
  for (const allocation of input.allocations) {
    if (allocation.deletedAt !== undefined) continue;
    const key = dayKey(allocation.resourceId, allocation.date);
    plannedByCell.set(key, (plannedByCell.get(key) ?? 0) + allocation.quarterHours);
  }

  const inspectionByCell = new Map<string, DailyInspection>();
  for (const inspection of input.inspections) {
    inspectionByCell.set(dayKey(inspection.resourceId, inspection.date), inspection);
  }

  const rows: ResourceDayHours[] = [];
  for (const resourceId of input.resourceIds) {
    for (const day of input.days) {
      const date = day.toString();
      const key = dayKey(resourceId, date);
      const available = input.availableOn(resourceId, day);
      const inspection = inspectionByCell.get(key);
      const allocated = plannedByCell.get(key) ?? 0;

      // O planejado do dia conferido é o **congelado**: depois do ajuste, a alocação já
      // não sabe mais o que estava planejado antes dele.
      const planned = inspection === undefined ? allocated : inspection.plannedQuarterHours;

      const hasSomething = planned > 0 || inspection !== undefined || (available ?? 0) > 0;
      if (!hasSomething) continue;

      rows.push({
        resourceId,
        date,
        ...(available === undefined ? {} : { availableQuarterHours: available }),
        plannedQuarterHours: planned,
        ...(inspection?.executedQuarterHours === undefined
          ? {}
          : { executedQuarterHours: inspection.executedQuarterHours }),
        status: inspection?.status ?? 'NOT_INSPECTED',
      });
    }
  }

  return rows;
}

// ── Totais ──────────────────────────────────────────────────────────────────────

export interface HoursTotals {
  /** Soma apenas das células com capacidade declarada. */
  readonly availableQuarterHours: number;
  readonly plannedQuarterHours: number;
  /** Soma do que já foi conferido. Dias pendentes não entram. */
  readonly executedQuarterHours: number;
  /** Disponível − planejado. Negativo é sobrecarga, e isso é informação legítima. */
  readonly balanceQuarterHours: number;
  /** Σ max(0, disponível − planejado) — hora de gente disponível que ninguém planejou. */
  readonly idleQuarterHours: number;
  /** Σ max(0, planejado − disponível) — o outro lado do mesmo saldo. */
  readonly overloadQuarterHours: number;
  /** Células sem capacidade declarada: o disponível do período é parcial (RN-047). */
  readonly unlimitedCells: number;
  /** Células já conferidas — o denominador honesto do "realizado". */
  readonly inspectedCells: number;
  readonly totalCells: number;
  /**
   * Planejado **só nas células conferidas** — o par justo do realizado.
   *
   * Comparar o realizado com o planejado do período inteiro faria uma semana em curso
   * parecer uma semana de baixa produção, quando o que falta é conferir os dias que
   * ainda não chegaram. Este é o planejado dos mesmos dias que o realizado soma.
   */
  readonly plannedInspectedQuarterHours: number;
}

export function totalsOf(rows: readonly ResourceDayHours[]): HoursTotals {
  let available = 0;
  let planned = 0;
  let plannedInspected = 0;
  let executed = 0;
  let idle = 0;
  let overload = 0;
  let unlimitedCells = 0;
  let inspectedCells = 0;

  for (const row of rows) {
    planned += row.plannedQuarterHours;
    if (row.executedQuarterHours !== undefined) {
      executed += row.executedQuarterHours;
      plannedInspected += row.plannedQuarterHours;
      inspectedCells += 1;
    }
    if (row.availableQuarterHours === undefined) {
      unlimitedCells += 1;
      continue;
    }
    available += row.availableQuarterHours;
    const balance = row.availableQuarterHours - row.plannedQuarterHours;
    if (balance > 0) idle += balance;
    else overload += -balance;
  }

  return {
    availableQuarterHours: available,
    plannedQuarterHours: planned,
    executedQuarterHours: executed,
    balanceQuarterHours: available - planned,
    idleQuarterHours: idle,
    overloadQuarterHours: overload,
    unlimitedCells,
    inspectedCells,
    totalCells: rows.length,
    plannedInspectedQuarterHours: plannedInspected,
  };
}

// ── Agrupamento no tempo ────────────────────────────────────────────────────────

export type ReportGrain = 'DAY' | 'WEEK' | 'MONTH';

export const GRAIN_LABEL: Readonly<Record<ReportGrain, string>> = {
  DAY: 'Dia',
  WEEK: 'Semana',
  MONTH: 'Mês',
};

export interface PeriodBucket {
  readonly key: string;
  readonly label: string;
  readonly from: string;
  readonly to: string;
  readonly totals: HoursTotals;
}

/**
 * Chave do balde a que um dia pertence.
 *
 * O mês sai da **data de negócio** (RN-124) e a semana, do **ano ISO** (RN-122) — nunca
 * do instante UTC nem do ano civil. 31/08 e 01/09 caem em meses diferentes mesmo às 23h
 * de São Paulo, e 30/12/2025 pertence à semana 1 de 2026.
 */
export function bucketKeyOf(date: BusinessDate, grain: ReportGrain): string {
  if (grain === 'DAY') return date.toString();
  if (grain === 'MONTH') return date.monthKey();
  return IsoWeek.of(date).key();
}

const MONTH_LABEL = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

function bucketLabelOf(date: BusinessDate, grain: ReportGrain): string {
  // `18/08` em vez de `18/08/2026`: o eixo tem espaço para cinco caracteres, e o ano já
  // está escrito no cabeçalho do período.
  if (grain === 'DAY') return date.toDisplay().slice(0, 5);
  if (grain === 'MONTH') {
    const [year, month] = date.monthKey().split('-');
    return `${MONTH_LABEL[Number(month) - 1] ?? month}/${(year ?? '').slice(2)}`;
  }
  const week = IsoWeek.of(date);
  return `S${String(week.isoWeek).padStart(2, '0')}`;
}

/** Agrupa as células no tempo, em ordem cronológica e sem balde vazio no meio. */
export function groupByPeriod(
  rows: readonly ResourceDayHours[],
  grain: ReportGrain,
): PeriodBucket[] {
  const buckets = new Map<
    string,
    { label: string; from: string; to: string; rows: ResourceDayHours[] }
  >();

  for (const row of rows) {
    const date = BusinessDate.of(row.date);
    const key = bucketKeyOf(date, grain);
    const existing = buckets.get(key);
    if (existing === undefined) {
      buckets.set(key, {
        label: bucketLabelOf(date, grain),
        from: row.date,
        to: row.date,
        rows: [row],
      });
      continue;
    }
    existing.rows.push(row);
    if (row.date < existing.from) existing.from = row.date;
    if (row.date > existing.to) existing.to = row.date;
  }

  return [...buckets.entries()]
    .map(([key, bucket]) => ({
      key,
      label: bucket.label,
      from: bucket.from,
      to: bucket.to,
      totals: totalsOf(bucket.rows),
    }))
    .sort((a, b) => a.from.localeCompare(b.from));
}

export interface ResourceBucket {
  readonly resourceId: ExecutionResourceId;
  readonly totals: HoursTotals;
}

export function groupByResource(rows: readonly ResourceDayHours[]): ResourceBucket[] {
  const byResource = new Map<ExecutionResourceId, ResourceDayHours[]>();
  for (const row of rows) {
    const list = byResource.get(row.resourceId);
    if (list === undefined) byResource.set(row.resourceId, [row]);
    else list.push(row);
  }
  return [...byResource.entries()].map(([resourceId, list]) => ({
    resourceId,
    totals: totalsOf(list),
  }));
}

// ── Desvio ──────────────────────────────────────────────────────────────────────

export interface Deviation {
  readonly plannedQuarterHours: number;
  readonly executedQuarterHours: number;
  readonly deltaQuarterHours: number;
  /**
   * Desvio percentual. **Ausente quando não havia plano**: 3h executadas sobre 0h
   * planejadas não são "300% acima" nem "infinito" — são 3h que ninguém planejou, e o
   * número absoluto já diz isso.
   */
  readonly deltaPct?: number;
}

export function deviationOf(planned: number, executed: number): Deviation {
  const delta = executed - planned;
  return {
    plannedQuarterHours: planned,
    executedQuarterHours: executed,
    deltaQuarterHours: delta,
    ...(planned === 0 ? {} : { deltaPct: (delta / planned) * 100 }),
  };
}
